// Mimin: Digitelle-fit som konfigurerbar modell (P4).
//
// Evne svarer på «kan de betale». Fit svarer på «er de vår type kunde» — to
// forskjellige spørsmål, og et konsern med 500 ansatte svarer ja på det første
// og nei på det andre.
//
// Første skive (P11) hadde bare størrelse, hardkodet. Her blir modellen
// konfigurerbar og får tre dimensjoner til: bransje, geografi og plattform.
// Konfigurasjonen ligger i `configuration.config.prospektFit` — samme JSONB-
// dokument som resten av innstillingene, så dette er ingen skjemaendring.
//
// TO PRINSIPPER STYRER FORMEN:
//
// 1. **Tom konfigurasjon oppfører seg nøyaktig som før.** Standardverdiene
//    under har tomme lister, og en tom liste er nøytral — ikke «ingenting
//    passer». Uten dette ville modellen begynt å rangere om av seg selv den
//    dagen koden ble deployet, før noen hadde bestemt hva Digitelle faktisk
//    foretrekker.
//
// 2. **Det vi ikke har klassifisert, straffes ikke.** En næringskode som ikke
//    står i lista er nøytral, ikke uønsket. Samme regel som i WP-tabellen: en
//    ufullstendig liste skal under-rapportere, aldri anklage. Vil man aktivt
//    nedprioritere noe, må man skrive det inn med vekten «unnga».
//
// Ingen Deno-avhengigheter — ren TypeScript, så modellen kan enhetstestes i
// Node og importeres av frontenden (@shared/fit.ts).

/** Hvordan en oppført bransje eller plattform skal vektes. */
export type FitVekt = "erfaring" | "onsker" | "unnga";

export type FitNaering = { prefiks: string; vekt: FitVekt };
export type FitPlattform = { verdi: PlattformNokkel; vekt: FitVekt };

/** Plattformene lista og filtrene allerede kjenner. */
export type PlattformNokkel = "wordpress" | "builder" | "ingen" | "annet";

export type FitStorrelse = {
  /** Under dette antallet ansatte er kjernemålgruppa. */
  kjerneUnder: number;
  /** Fra dette antallet endrer anskaffelsen karakter. */
  storFra: number;
  /** Fra dette antallet er kunden sjelden tilgjengelig for et lite byrå. */
  svaertStorFra: number;
};

export type ProspektFitConfig = {
  /** 2-sifret NACE-prefiks (som i filtermenyen), med vekt. */
  naeringer: FitNaering[];
  /** Kommunenummer vi foretrekker. Tom liste = ingen geografisk preferanse. */
  kommuner: string[];
  storrelse: FitStorrelse;
  plattformer: FitPlattform[];
};

/**
 * Standard: bare størrelse, med grensene som gjaldt før P4. Alt annet tomt.
 * Endres dette til noe annet enn nøytralt, rangerer alle installasjoner om
 * uten at noen har bedt om det — se prinsipp 1 over.
 */
export const STANDARD_FIT_CONFIG: ProspektFitConfig = {
  naeringer: [],
  kommuner: [],
  storrelse: { kjerneUnder: 20, storFra: 51, svaertStorFra: 250 },
  plattformer: [],
};

// Faktorene per vekt. Bonusene er bevisst små: fit skal justere rekkefølgen
// mellom prospekter med sammenliknbar smerte, ikke løfte et problemfritt
// nettsted over et ødelagt ett. «unnga» er kraftigere enn bonusene fordi et
// eksplisitt fravalg er en sterkere uttalelse enn en preferanse.
export const VEKT_FAKTOR: Record<FitVekt, number> = {
  erfaring: 1.15,
  onsker: 1.1,
  unnga: 0.5,
};

export const VEKT_LABEL: Record<FitVekt, string> = {
  erfaring: "erfaring med",
  onsker: "ønsker mer av",
  unnga: "unngå",
};

/** Bonus for en kommune vi foretrekker. Ingen straff for de øvrige. */
export const KOMMUNE_FAKTOR = 1.1;

// Ytre grenser. Fit er et korrektiv, ikke en ny hovedscore: uansett hvordan
// konfigurasjonen settes opp skal den ikke kunne mangedoble eller utslette et
// prospekt på egen hånd. Kill-switchen er det eneste som nullstiller.
export const FIT_MIN = 0.25;
export const FIT_MAKS = 1.5;

/** Én dimensjons bidrag, slik detaljpanelet kan vise hva som traff. */
export type FitDel = { hva: string; faktor: number; tekst: string };

export type FitResultat = {
  faktor: number;
  signal: string | null;
  deler: FitDel[];
};

export const FIT_NOYTRAL: FitResultat = { faktor: 1, signal: null, deler: [] };

/** Fakta fit-modellen trenger. Bevisst løsere enn EntityFacts. */
export type FitFakta = {
  antall_ansatte: number | null;
  naeringskode: string | null;
  kommunenr: string | null;
  plattform: PlattformNokkel | null;
};

/**
 * Klassifiser plattformen fra tech-objektet. Én definisjon, delt mellom
 * fit-modellen og filtermenyen (`plattformOf` i prospectFilterUtils).
 */
export function plattformAv(
  tech: Record<string, unknown> | null | undefined,
  harUrl: boolean,
): PlattformNokkel {
  const t = tech ?? {};
  if (t.cms === "WordPress") return "wordpress";
  if (t.builder) return "builder";
  if (!harUrl || t.parked || t.no_site || t.unreachable) return "ingen";
  return "annet";
}

/** Slå sammen lagret konfigurasjon med standarden, felt for felt. */
export function fitConfigAv(
  lagret: Partial<ProspektFitConfig> | null | undefined,
): ProspektFitConfig {
  const l = lagret ?? {};
  return {
    naeringer: l.naeringer ?? STANDARD_FIT_CONFIG.naeringer,
    kommuner: l.kommuner ?? STANDARD_FIT_CONFIG.kommuner,
    storrelse: { ...STANDARD_FIT_CONFIG.storrelse, ...(l.storrelse ?? {}) },
    plattformer: l.plattformer ?? STANDARD_FIT_CONFIG.plattformer,
  };
}

// Størrelsen dempes i stedet for å siles bort: store selskaper havner nederst
// i lista, ikke utenfor den, så et stort selskap med et virkelig elendig
// nettsted fortsatt kan vurderes.
function storrelsesDel(ansatte: number | null, s: FitStorrelse): FitDel | null {
  // Ukjent ansatt-tall dempes ikke — vi straffer ikke det vi ikke vet.
  if (ansatte == null || ansatte < s.kjerneUnder) return null;
  if (ansatte < s.storFra) {
    return {
      hva: "storrelse",
      faktor: 0.85,
      tekst: `Litt over kjernemålgruppa (${ansatte} ansatte)`,
    };
  }
  if (ansatte < s.svaertStorFra) {
    return {
      hva: "storrelse",
      faktor: 0.6,
      tekst: `Større enn målgruppa (${ansatte} ansatte)`,
    };
  }
  return {
    hva: "storrelse",
    faktor: 0.3,
    tekst: `Mye større enn målgruppa (${ansatte} ansatte)`,
  };
}

function naeringsDel(
  naeringskode: string | null,
  liste: FitNaering[],
): FitDel | null {
  if (!naeringskode || liste.length === 0) return null;
  // Lengst prefiks vinner, så «47.11» kan overstyre «47».
  const treff = liste
    .filter((n) => n.prefiks && naeringskode.startsWith(n.prefiks))
    .sort((a, b) => b.prefiks.length - a.prefiks.length)[0];
  if (!treff) return null;
  return {
    hva: "bransje",
    faktor: VEKT_FAKTOR[treff.vekt],
    tekst: `Bransje ${treff.prefiks}: ${VEKT_LABEL[treff.vekt]}`,
  };
}

function kommuneDel(kommunenr: string | null, liste: string[]): FitDel | null {
  if (!kommunenr || liste.length === 0 || !liste.includes(kommunenr)) {
    return null;
  }
  return {
    hva: "geografi",
    faktor: KOMMUNE_FAKTOR,
    tekst: "I foretrukket område",
  };
}

function plattformDel(
  plattform: PlattformNokkel | null,
  liste: FitPlattform[],
): FitDel | null {
  if (!plattform) return null;
  const treff = liste.find((p) => p.verdi === plattform);
  if (!treff) return null;
  return {
    hva: "plattform",
    faktor: VEKT_FAKTOR[treff.vekt],
    tekst: `Plattform ${plattform}: ${VEKT_LABEL[treff.vekt]}`,
  };
}

const rund2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Regn fit for ett prospekt. Dimensjonene multipliseres, og hver dimensjon som
 * faktisk traff blir liggende i `deler` så UI kan vise hvorfor.
 */
export function fitAv(
  f: FitFakta,
  config: ProspektFitConfig = STANDARD_FIT_CONFIG,
): FitResultat {
  const deler = [
    storrelsesDel(f.antall_ansatte, config.storrelse),
    naeringsDel(f.naeringskode, config.naeringer),
    kommuneDel(f.kommunenr, config.kommuner),
    plattformDel(f.plattform, config.plattformer),
  ].filter((d): d is FitDel => d !== null);

  if (deler.length === 0) return FIT_NOYTRAL;

  const rå = deler.reduce((a, d) => a * d.faktor, 1);
  const faktor = rund2(Math.min(FIT_MAKS, Math.max(FIT_MIN, rå)));

  return {
    faktor,
    // Rekkefølgen i signalet følger dempingen: det som trekker mest ned nevnes
    // først, fordi det er den opplysningen selgeren trenger raskest.
    signal: [...deler].sort((a, b) => a.faktor - b.faktor)[0].tekst,
    deler,
  };
}
